<template>
  <section class="flex flex-col min-h-0 h-full rounded-[12px] bg-[#161c22] border border-[#232a31] p-4" data-testid="transcript-panel">
    <header class="flex items-center gap-2 mb-3">
      <lib-icon name="files/file/outline" size="18" color="#6CBFFF" />
      <h2 class="text-[15px] font-semibold text-white truncate">{{ translate('transcription_title', 'Transcription') }}</h2>
    </header>

    <input
      v-model="searchQuery"
      type="text"
      name="transcript-search"
      class="border border-[#3a4149] rounded-[8px] bg-transparent text-white text-[14px] h-[38px] w-full px-3 outline-none focus:border-primary placeholder:text-white/40 disabled:opacity-50"
      :placeholder="translate('transcript_search_placeholder', 'Search transcript')"
      :disabled="!transcript"
      data-testid="transcript-search"
    />

    <div
      ref="scrollContainer"
      class="flex-1 min-h-[320px] mt-3 rounded-[10px] bg-[#0c1116] overflow-y-auto scrollbar-thin scrollbar-thumb-[#f0f6ff57] scrollbar-track-transparent px-4 py-3"
      data-testid="transcript-messages"
    >
      <div v-if="loading" class="flex flex-col gap-4 mt-2">
        <div v-for="n in 4" :key="n" class="flex flex-col gap-2">
          <div class="h-3 w-24 rounded bg-white/10 animate-pulse"></div>
          <div class="h-3 rounded bg-white/5 animate-pulse"></div>
          <div class="h-3 w-3/4 rounded bg-white/5 animate-pulse"></div>
        </div>
      </div>

      <template v-else-if="transcript">
        <div v-if="firstTime !== null && !searchQuery" class="border-l-2 border-primary pl-2 text-[11px] text-primary mb-3">
          {{ translate('transcript_session_starts', 'Session starts') }} · {{ formatTimecode(firstTime) }}
        </div>

        <div
          v-for="msg in visibleMessages"
          :key="msg.time"
          :data-time="msg.time"
          class="rounded-[8px] px-2.5 py-2 mb-2 transition-colors duration-300"
          :class="isHighlighted(msg) ? 'bg-primary text-[#0c1116]' : 'text-white/89'"
          data-testid="transcript-message"
        >
          <div class="flex justify-between items-baseline gap-3 mb-1 text-[12px] font-semibold">
            <span :class="isHighlighted(msg) ? 'text-[#0c1116]' : 'text-white'">{{ msg.display_name }}</span>
            <span class="font-normal tabular-nums" :class="isHighlighted(msg) ? 'text-[#0c1116]' : 'text-white/75'">{{ formatTimecode(msg.time) }}</span>
          </div>
          <div class="text-[13px] leading-[19px]">{{ msg.source }}</div>
          <div v-for="(translation, tIdx) in msg.translations || []" :key="tIdx" class="text-[13px] leading-[19px] opacity-75 ml-3 mt-1">
            {{ translation.text }}
          </div>
        </div>

        <div v-if="searchQuery && !visibleMessages.length" class="text-center text-[13px] text-white/75 mt-6">
          {{ $t('no_results_for_message') }} "{{ searchQuery }}"
        </div>
      </template>
    </div>

    <footer class="border-t border-[#232a31] mt-3 pt-3 text-[11px] text-white/75 min-h-[28px]">
      <template v-if="transcript && transcript.recording_duration">
        {{ translate('transcript_length', 'Transcript length') }} · {{ formatDurationLabel(Math.round(transcript.recording_duration / 60)) }}
      </template>
    </footer>
  </section>
</template>

<script setup>
import { computed, defineProps, nextTick, ref, watch } from 'vue';
import LibIcon from '@/components/ui/LibIcon.vue';
import { translate } from '@/composables/useCaptionSettings';
import { formatDurationLabel, formatTimecode } from '@/utils/transcript-date';

const props = defineProps({
  transcript: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  // { start, end?, nonce }: messages in [start, end] are highlighted and scrolled into view.
  highlight: { type: Object, default: null },
});

const searchQuery = ref('');
const scrollContainer = ref(null);

const messages = computed(() => props.transcript?.messages || []);
const firstTime = computed(() => (messages.value.length ? messages.value[0].time : null));

const visibleMessages = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return messages.value;
  return messages.value.filter(msg =>
    msg.source.toLowerCase().includes(query) || msg.display_name.toLowerCase().includes(query)
  );
});

function isHighlighted(msg) {
  if (!props.highlight) return false;
  const { start, end = start } = props.highlight;
  return msg.time >= start && msg.time <= end;
}

watch(() => props.transcript?.conference_id, () => {
  searchQuery.value = '';
  if (scrollContainer.value) scrollContainer.value.scrollTop = 0;
});

watch(() => props.highlight?.nonce, async () => {
  if (!props.highlight) return;
  searchQuery.value = '';
  await nextTick();
  const target = [...scrollContainer.value.querySelectorAll('[data-time]')]
    .find(el => Number(el.dataset.time) >= props.highlight.start);
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
});
</script>
